<script setup lang="ts">
import { computed } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';

import KaCopyButton from './KaCopyButton.vue';

/** Props shared by every KaCopyLine, regardless of whether the label is visible. */
interface BaseCopyLineProps {
  /** Command shown in the line and copied to the clipboard. */
  command: string;
  id: string;
  successLabel?: string;
  errorLabel?: string;
  dataTestid?: string;
}

/**
 * Default mode: the label is visible beside the icon, so it is optional and
 * falls back to the "Copy" default.
 */
interface LabeledCopyLineProps extends BaseCopyLineProps {
  hideLabel?: false;
  size?: 'base';
  label?: string;
}

/**
 * Icon-only mode (`hideLabel` or `size="small"`) hides the visible text, so an
 * explicit, contextual `label` is required at the call site to give the copy
 * button a meaningful accessible name (K10-36264). Making `label` required here
 * turns the omission into a compile-time error; the DEV guard below still covers
 * the case types cannot see, a caller passing the literal `label="Copy"`.
 */
interface IconOnlyCopyLineProps extends BaseCopyLineProps {
  hideLabel?: boolean;
  size?: 'base' | 'small';
  label: string;
}

export type KaCopyLineProps = LabeledCopyLineProps | IconOnlyCopyLineProps;

const props = withDefaults(defineProps<KaCopyLineProps>(), {
  label: 'Copy',
  successLabel: 'Copied',
  hideLabel: false,
  size: 'base',
});

const emit = defineEmits<{
  'copy:success': [];
  'copy:error': [msg: unknown];
}>();

const { dataTestid, suffix } = useDataTestid(computed(() => props.dataTestid ?? props.id));
const shouldHideLabel = computed(() => props.hideLabel || props.size === 'small');

if (import.meta.env.DEV && shouldHideLabel.value && props.label === 'Copy') {
  // Icon-only mode hides the label, so the default "Copy" is an uninformative
  // accessible name. One-time check: size/label are static component config.
  throw new Error(
    '[KaCopyLine] Icon-only mode (`hideLabel` or `size="small"`) requires an explicit ' +
      '`label` (e.g. `label="Copy cluster ID"`); the default "Copy" gives the copy button ' +
      'an uninformative accessible name.',
  );
}
</script>
<template>
  <div
    :id="props.id"
    :data-testid="dataTestid"
    class="relative inline-flex w-full items-center justify-center overflow-hidden rounded border border-gray-300 bg-gray-50 p-0 dark:border-navy-600 dark:bg-navy-800"
  >
    <code
      :data-testid="suffix('copy-content')"
      :class="[
        'block w-full truncate border-gray-300 font-mono font-normal text-gray-900 dark:text-gray-50',
        props.size === 'small' ? 'px-2 py-1 text-xs' : 'px-5 py-2 text-sm',
      ]"
    >
      <slot>{{ props.command }}</slot>
    </code>

    <KaCopyButton
      :id="`${props.id}-copy-button`"
      :data-testid="suffix('copy-button')"
      :label="props.label"
      :hide-label="shouldHideLabel"
      :command="props.command"
      :success-label="props.successLabel"
      :error-label="props.errorLabel"
      :class="[
        'gap-1 rounded-l-none border-y-0 border-r-0 focus-visible:outline-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400',
        shouldHideLabel ? 'w-auto' : 'w-24',
        props.size === 'small' ? 'p-1' : 'p-3 pl-2.5',
      ]"
      @copy:success="emit('copy:success')"
      @copy:error="emit('copy:error', $event)"
    />
  </div>
</template>
